// frontend/src/components/PagamentoParcialModal/PagamentoParcialModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, DatePicker, Button, Alert, Row, Col, InputNumber, message } from 'antd';
import { createPagamentoParcial, updatePagamentoParcial, getFaturasDisponiveis } from '../../services/cardService';
import { getContas } from '../../services/accountService';
import dayjs from 'dayjs';
import { getErrorMessage } from '../../utils/errorHandler';
import { getMobileFullscreenModalProps } from '../../utils/mobileModal';

const { Option } = Select;

// ADICIONADO: isMobile prop
const PagamentoParcialModal = ({ isOpen, onClose, onSave, fatura, transacaoToEdit, isMobile = false }) => {
    const [form] = Form.useForm();
    const [contas, setContas] = useState([]);
    const [faturasDisponiveisState, setFaturasDisponiveisState] = useState([]);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    
    const isEditing = transacaoToEdit !== null;

    useEffect(() => {
        if (isOpen) {
            setIsLoading(true);
            setError('');
            form.resetFields();

            Promise.all([
                getContas(),
                getFaturasDisponiveis(fatura.cartao.id, fatura.id)
            ])
            .then(([contasData, faturasData]) => {
                setContas(contasData.sort((a, b) => a.nome.localeCompare(b.nome)));
                setFaturasDisponiveisState(faturasData);

                if (isEditing) {
                    form.setFieldsValue({
                        descricao: transacaoToEdit.descricao,
                        valor: parseFloat(transacaoToEdit.valor),
                        data: transacaoToEdit.data ? dayjs(transacaoToEdit.data) : null,
                        fatura_id: transacaoToEdit.fatura,
                        conta_id: transacaoToEdit.transacao_espelho_conta_id,
                    });
                } else {
                    form.setFieldsValue({
                        descricao: 'Pagamento Parcial de Fatura',
                        data: dayjs(),
                        fatura_id: fatura.id,
                        valor: null,
                        conta_id: null,
                    });
                }
            })
            .catch((err) => setError(getErrorMessage(err, 'Não foi possível carregar os dados necessários.')))
            .finally(() => setIsLoading(false));
        }
    }, [isOpen, isEditing, transacaoToEdit, fatura, form]);

    const onFinish = async (values) => {
        setIsLoading(true);
        setError('');

        const pagamentoData = {
            fatura_id: values.fatura_id,
            conta_id: values.conta_id,
            valor: values.valor,
            data_pagamento: values.data ? dayjs(values.data).format('YYYY-MM-DD') : null,
            descricao: values.descricao,
        };

        try {
            if (isEditing) {
                await updatePagamentoParcial(transacaoToEdit.id, pagamentoData);
                onSave(true, 'Pagamento atualizado com sucesso!');
            } else {
                await createPagamentoParcial(pagamentoData);
                onSave(true, 'Pagamento criado com sucesso!');
            }
        } catch (err) {
            const errorMessage = getErrorMessage(err, "Ocorreu um erro ao salvar o pagamento.");
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => { if (!isLoading) onClose(); };
    const filterOption = (input, option) => (option?.children[0] ?? '').toLowerCase().includes(input.toLowerCase());

    // Estilos responsivos
    const inputSize = isMobile ? 'large' : 'middle';
    const modalWidth = isMobile ? '100%' : 520;

    const renderFooter = () => {
        if (isMobile) {
            return (
                <Row gutter={[8, 8]}>
                    <Col span={12}>
                        <Button block size="large" onClick={handleCancel} disabled={isLoading}>Cancelar</Button>
                    </Col>
                    <Col span={12}>
                        <Button block size="large" type="primary" loading={isLoading} onClick={() => form.submit()}>{isEditing ? "Salvar" : "Salvar"}</Button>
                    </Col>
                </Row>
            );
        }
        return [
            <Button key="back" onClick={handleCancel} disabled={isLoading}>Cancelar</Button>,
            <Button key="submit" type="primary" loading={isLoading} onClick={() => form.submit()}>{isEditing ? "Salvar Alterações" : "Salvar Pagamento"}</Button>,
        ];
    };

    return (
        <Modal
            open={isOpen}
            title={isEditing ? "Editar Pagamento Parcial" : "Pagamento Parcial de Fatura"}
            onCancel={handleCancel}
            destroyOnClose
            width={modalWidth}
            {...getMobileFullscreenModalProps(isMobile)}
            footer={renderFooter()}
        >
            <Form form={form} layout="vertical" onFinish={onFinish} preserve={false} size={inputSize}>
                {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}
                
                <Row gutter={16}>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item name="valor" label="Valor do Pagamento" rules={[{ required: true, message: 'O valor é obrigatório' }]}>
                            <InputNumber
                                prefix="R$ "
                                style={{ width: '100%' }}
                                precision={2}
                                step={0.01}
                                inputMode="decimal" // Teclado numérico
                                formatter={(value) => {
                                    if (value === null || value === undefined || value === '') return '';
                                    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
                                }}
                                parser={(value) => {
                                    if (!value) return null;
                                    const onlyNumbers = value.replace(/[^\d]/g, '');
                                    if (!onlyNumbers) return null;
                                    const num = parseInt(onlyNumbers, 10);
                                    return num / 100;
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item name="data" label="Data do Pagamento" rules={[{ required: true, message: 'A data é obrigatória' }]}>
                            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item name="conta_id" label="Debitar da Conta" rules={[{ required: true, message: 'Selecione uma conta' }]}>
                    <Select showSearch placeholder="Selecione uma conta..." loading={isLoading} optionFilterProp="children" filterOption={filterOption}>
                        {contas.map(conta => (
                            <Option key={conta.id} value={conta.id}>
                                {`${conta.nome} (${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(conta.saldo_atual)})`}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
                
                <Form.Item name="fatura_id" label="Fatura" rules={[{ required: true, message: 'Selecione uma fatura' }]}>
                    <Select placeholder="Selecione uma fatura..." loading={isLoading}>
                        {faturasDisponiveisState.map(f => (
                            <Option key={f.id} value={f.id}>{f.nome}</Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item name="descricao" label="Descrição" rules={[{ required: true, message: 'A descrição é obrigatória' }]}>
                    <Input />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default PagamentoParcialModal;